import { forwardRef, memo, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { ChevronDown, Eye } from 'lucide-react'
import { Avatar, Popover, Tooltip } from '@/components/play'
import { formatAudience, initialsSource } from '@/lib/watch/rules'
import { usePresence } from '@/lib/watch/usePresence'
import type { Online, OnlinePerson } from '@/lib/watch/useOnline'
import { cn } from '@/lib/utils'
import './watch-together.css'

/** The seats the collapsed row draws; it shows as many whole ones as fit (the stylesheet wraps the rest out of sight). */
export const ROW_SEATS = 40
/** How long a seat takes to fade and close up after its person leaves (the stylesheet's seat-out). */
export const SEAT_EXIT_MS = 320

const personKey = (person: OnlinePerson) => person.key

/** Who a seat is, for a tooltip and a screen reader. */
function seatName(person: OnlinePerson) {
  if (person.you) return 'You'
  const name = person.name ?? 'Guest'
  return person.host ? `${name}, host` : name
}

/** One seat: their photo, else initials in their tone, else the neutral glyph; a live host's carries the live ring. */
function Seat({ person, state }: { person: OnlinePerson; state: 'here' | 'enter' | 'out' }) {
  return (
    <li className="watch-audience__seat" data-state={state === 'here' ? undefined : state} data-you={person.you || undefined}>
      <Avatar
        name={person.name || person.handle ? initialsSource(person.name, person.handle) : undefined}
        src={person.avatarUrl}
        size="sm"
        tone="auto"
        toneKey={person.toneKey}
        live={person.host}
        title={seatName(person)}
      />
      <span className="sr-only">{seatName(person)}</span>
    </li>
  )
}

/** Seats with their arrivals and departures (`usePresence`): arrivals grow in, departures fade and close up. */
function Seats({ people, className, children }: { people: readonly OnlinePerson[]; className: string; children?: ReactNode }) {
  const seats = usePresence(people, personKey, SEAT_EXIT_MS)
  return (
    <ul className={className}>
      {seats.map(({ key, item, state }) => (
        <Seat key={key} person={item} state={state} />
      ))}
      {children}
    </ul>
  )
}

type AudienceToggleProps = ButtonHTMLAttributes<HTMLButtonElement> & { online: Online; seats: readonly OnlinePerson[] }

/** The row's button: how many watch in all beside the eye, the seats that fit, and the chevron. The popover's trigger. */
const AudienceToggle = forwardRef<HTMLButtonElement, AudienceToggleProps>(function AudienceToggle({ online, seats, className, ...props }, ref) {
  return (
    <Tooltip content="Audience">
      <button
        ref={ref}
        type="button"
        className={cn('watch-audience__toggle', className)}
        aria-label={`${formatAudience(online.count)} watching. Show the audience`}
        {...props}
      >
        <span className="watch-audience__count" aria-hidden="true">
          <Eye />
          {formatAudience(online.count)}
        </span>
        <span className="watch-audience__row" aria-hidden="true">
          <Seats people={seats} className="watch-audience__seats" />
        </span>
        <ChevronDown className="watch-audience__chevron" aria-hidden="true" />
      </button>
    </Tooltip>
  )
})

export interface AudienceProps {
  /** Who is online (`useOnline`); the page shows none before the room connects. */
  online: Online
  /** At the row's end: the reaction bar. */
  actions?: ReactNode
  /** `chat`: under the chat's head, on the chat's surface. `stage`: the desktop's row under the video. */
  placement?: 'chat' | 'stage'
  defaultOpen?: boolean
  className?: string
}

/**
 * Who is watching, as an audience (founder, 27 October 2026; docs/features/watch-together.md). Collapsed, one row: how
 * many watch in all beside the eye, then seats side by side, never overlapping, as many whole ones as fit (hosts with
 * the live ring first, then you, then everyone else newest first), and a chevron. People arriving grow into the
 * front of the row and people leaving fade where they sat, so you see the room fill and empty. A press opens the whole
 * audience as a block of seats over the page (Play's popover), up to `AUDIENCE_SEATS`, then one "+N" seat, so
 * thousands watching still draw quickly. The reaction bar can ride at the row's end.
 */
export const Audience = memo(function Audience({ online, actions, placement = 'chat', defaultOpen = false, className }: AudienceProps) {
  const rest = online.count - online.audience.length
  return (
    <div className={cn('watch-audience', className)} data-placement={placement}>
      <Popover
        trigger={<AudienceToggle online={online} seats={online.audience.slice(0, ROW_SEATS)} />}
        label="Audience"
        side={placement === 'stage' ? 'top' : 'bottom'}
        align="start"
        defaultOpen={defaultOpen}
        className="watch-audience__popover"
      >
        <Seats people={online.audience} className="watch-audience__hall">
          {rest > 0 && (
            <li className="watch-audience__more">
              +{formatAudience(rest)}
              <span className="sr-only"> more watching</span>
            </li>
          )}
        </Seats>
      </Popover>
      {actions}
    </div>
  )
})
